import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

export default function Register() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const nextPath = searchParams.get('next');
  const afterAuthTarget = nextPath
    ? `${nextPath}${searchParams.get('resume') ? (nextPath.includes('?') ? '&' : '?') + 'resume=1' : ''}`
    : '/dashboard';

  const initialRole = searchParams.get('role') || 'buyer';
  const initialVendorType = 'normal';

  const [form, setForm] = useState({
    fullName: searchParams.get('fullName') || '',
    email: searchParams.get('email') || '',
    password: '',
    confirmPassword: '',
    mobileNumber: searchParams.get('mobileNumber') || '',
    role: initialRole,
    vendorType: initialVendorType,
  });

  const [otp, setOtp] = useState('');
  const [registrationToken, setRegistrationToken] = useState('');
  const [showOtpStep, setShowOtpStep] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [mobileError, setMobileError] = useState('');

  const handleEmailBlur = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const normalizedEmail = form.email ? form.email.trim().toLowerCase() : '';
    if (!normalizedEmail) {
      setEmailError('');
      return;
    }
    if (!emailRegex.test(normalizedEmail)) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    try {
      const res = await authAPI.checkEmail(normalizedEmail);
      if (res.data.registered) {
        setEmailError('Email already registered.');
      } else {
        setEmailError('');
      }
    } catch (err) {
      console.error('Failed to check email registration status:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setEmailError('');
    setMobileError('');

    // 1. Full Name Validation
    const trimmedName = form.fullName ? form.fullName.trim() : '';
    if (trimmedName.length < 3 || trimmedName.length > 50) {
      return setError('Full Name must be between 3 and 50 characters.');
    }
    const nameRegex = /^[a-zA-Z\s.-]+$/;
    if (!nameRegex.test(trimmedName)) {
      return setError('Full Name can only contain letters, spaces, dots, or hyphens.');
    }

    // 2. Mobile Number Validation (Indian Mobile Format)
    let cleanedMobile = form.mobileNumber ? String(form.mobileNumber).replace(/\D/g, '') : '';
    if (cleanedMobile.length === 12 && cleanedMobile.startsWith('91')) {
      cleanedMobile = cleanedMobile.slice(2);
    } else if (cleanedMobile.length === 11 && cleanedMobile.startsWith('0')) {
      cleanedMobile = cleanedMobile.slice(1);
    }
    const mobileRegex = /^[6-9]\d{9}$/;
    if (!mobileRegex.test(cleanedMobile)) {
      setMobileError('Invalid Mobile Number.');
      return;
    }

    // 3. Email Validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const normalizedEmail = form.email ? form.email.trim().toLowerCase() : '';
    if (!emailRegex.test(normalizedEmail)) {
      setEmailError('Please enter a valid email address.');
      return;
    }

    if (emailError) {
      return;
    }

    // 4. Password Validation
    if (form.password.length < 6) {
      return setError('Please set a password of at least 6 characters.');
    }
    if (form.password !== form.confirmPassword) {
      return setError("Passwords don't match — please re-enter.");
    }

    setLoading(true);
    try {
      const res = await authAPI.register({
        fullName: trimmedName,
        email: normalizedEmail,
        password: form.password,
        mobileNumber: cleanedMobile,
        role: form.role,
        vendorType: form.vendorType,
      });
      setRegistrationToken(res.data.registrationToken);
      setShowOtpStep(true);
    } catch (err) {
      const errMsg = err.response?.data?.message || 'Registration failed.';
      if (errMsg.includes('Email already registered')) {
        setEmailError('Email already registered.');
      } else {
        setError(errMsg);
      }
    } finally { setLoading(false); }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    if (otp.length !== 6) return setError('Please enter a 6-digit OTP.');
    setLoading(true);
    try {
      const res = await authAPI.verifyRegistration({ registrationToken, otp });
      login(res.data.data.token, res.data.data.user);
      const target = afterAuthTarget !== '/dashboard' ? afterAuthTarget : (res.data.data.user.role === 'vendor' ? '/vendor/onboarding' : '/buyer/home');
      navigate(target);
    } catch (err) {
      setError(err.response?.data?.message || 'Verification failed.');
    } finally { setLoading(false); }
  };

  return (
    <div className="flex-stack" style={{ minHeight: '100vh', background: 'var(--cream)' }}>
      {/* Left — branding */}
      <div className="hide-mobile" style={{ flex: 1, background: 'var(--green-dk)', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '60px 50px' }}>
        <div>
          <span className="logo" style={{ color: '#fff', fontSize: 32 }}>Kanjusee</span>
          <span className="logo-dot" style={{ fontSize: 36 }}>.</span>
        </div>
        <p style={{ fontFamily: 'var(--font-serif)', fontSize: 26, fontWeight: 700, color: '#fff', marginTop: 24, lineHeight: 1.3 }}>
          Join India's fastest growing B2B marketplace.
        </p>

        <div style={{ marginTop: 32, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {[
            { icon: '🛍️', text: 'Buyers post demands for free' },
            { icon: '✅', text: 'Get quotes from verified vendors' },
            { icon: '💰', text: 'Save more on bulk purchases' },
            { icon: '⚡', text: 'Fast & secure marketplace' },
          ].map((f, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, color: 'rgba(255,255,255,0.75)', fontSize: 14 }}>
              <span style={{ fontSize: 20 }}>{f.icon}</span> {f.text}
            </div>
          ))}
        </div>
      </div>

      {/* Right — form */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 24px' }}>
        <div style={{ width: '100%', maxWidth: 400 }}>
          <div className="show-mobile" style={{ textAlign: 'center', marginBottom: 24 }}>
            <Link to="/" style={{ textDecoration: 'none' }}>
              <span className="logo" style={{ color: 'var(--green)', fontSize: 28 }}>Kanjusee</span>
              <span className="logo-dot" style={{ fontSize: 32 }}>.</span>
            </Link>
          </div>

          {!showOtpStep ? (
            <>
              <h2 className="heading-md" style={{ marginBottom: 4 }}>Create your account</h2>
              <p className="text-muted text-sm" style={{ marginBottom: 24 }}>It only takes a minute</p>

              {error && (
                <div style={{ background: 'var(--red-lt)', color: 'var(--red)', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 16, fontWeight: 500 }}>{error}</div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="grid-1-2" style={{ gap: 12 }}>
                  <div className="form-group">
                    <label className="form-label">Full Name</label>
                    <input className="form-input" required value={form.fullName} onChange={e => setForm({ ...form, fullName: e.target.value })} placeholder="Your name" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Mobile</label>
                    <input
                      className="form-input"
                      type="text"
                      maxLength={10}
                      value={form.mobileNumber}
                      onChange={e => {
                        const val = e.target.value.replace(/\D/g, '');
                        setForm({ ...form, mobileNumber: val });
                        if (val && !/^[6-9]/.test(val)) {
                          setMobileError('Invalid Mobile Number.');
                        } else if (val && val.length < 10) {
                          setMobileError('Mobile number must be exactly 10 digits.');
                        } else {
                          setMobileError('');
                        }
                      }}
                      placeholder="9876543210"
                    />
                    {mobileError && (
                      <div style={{ background: 'var(--red-lt)', color: 'var(--red)', padding: '6px 10px', borderRadius: 'var(--radius)', fontSize: '12px', marginTop: '6px', fontWeight: 500 }}>
                        {mobileError}
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Email Address</label>
                  <input
                    className="form-input"
                    type="email"
                    required
                    value={form.email}
                    onChange={e => {
                      setForm({ ...form, email: e.target.value });
                      setEmailError('');
                    }}
                    onBlur={handleEmailBlur}
                    placeholder="you@company.com"
                  />
                  {emailError && (
                    <div style={{ background: 'var(--red-lt)', color: 'var(--red)', padding: '6px 10px', borderRadius: 'var(--radius)', fontSize: '12px', marginTop: '6px', fontWeight: 500 }}>
                      {emailError}
                    </div>
                  )}
                </div>

                <div style={{ background: 'var(--green-lt, #E8F5EE)', borderLeft: '3px solid var(--green)', borderRadius: 6, padding: '10px 14px', marginBottom: 14 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--green)', marginBottom: 2 }}>🔐 Set your sign-in password</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)' }}>You'll use this to sign in from the web or mobile app.</div>
                </div>

                <div className="grid-1-2" style={{ gap: 12 }}>
                  <div className="form-group">
                    <label className="form-label">Password</label>
                    <input className="form-input" type="password" required value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Min 6 characters" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Confirm Password</label>
                    <input className="form-input" type="password" required value={form.confirmPassword} onChange={e => setForm({ ...form, confirmPassword: e.target.value })} placeholder="Re-enter password" />
                  </div>
                </div>

                <button type="submit" disabled={loading} className="btn btn-pri btn-lg btn-full" style={{ marginTop: 4 }}>
                  {loading ? 'Processing...' : `Sign Up as ${form.role.charAt(0).toUpperCase() + form.role.slice(1)} →`}
                </button>
              </form>
            </>
          ) : (
            <>
              <h2 className="heading-md" style={{ marginBottom: 4 }}>Verify your email</h2>
              <p className="text-muted text-sm" style={{ marginBottom: 24 }}>We've sent a 6-digit OTP to <b>{form.email}</b></p>

              {error && (
                <div style={{ background: 'var(--red-lt)', color: 'var(--red)', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 16, fontWeight: 500 }}>{error}</div>
              )}

              <form onSubmit={handleVerifyOtp}>
                <div className="form-group">
                  <label className="form-label">OTP Code</label>
                  <input
                    className="form-input"
                    required
                    maxLength={6}
                    style={{ letterSpacing: 8, fontSize: 24, textAlign: 'center', fontWeight: 700 }}
                    value={otp}
                    onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                  />
                </div>

                <button type="submit" disabled={loading} className="btn btn-pri btn-lg btn-full" style={{ marginTop: 4 }}>
                  {loading ? 'Verifying...' : 'Verify & Complete Registration'}
                </button>

                <p style={{ textAlign: 'center', marginTop: 16, fontSize: 13 }}>
                  Didn't get the code? <button type="button" onClick={handleSubmit} style={{ background: 'none', border: 'none', color: 'var(--green)', fontWeight: 700, cursor: 'pointer' }}>Resend</button>
                </p>
                <p style={{ textAlign: 'center', marginTop: 8 }}>
                  <button type="button" onClick={() => setShowOtpStep(false)} style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: 12, cursor: 'pointer' }}>← Back to edit details</button>
                </p>
              </form>
            </>
          )}

          <p style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: 'var(--muted)' }}>
            Already have an account?{' '}
            <Link to="/login" style={{ color: 'var(--green)', fontWeight: 700, textDecoration: 'none' }}>Sign In</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

